import React, { useState, useEffect, useCallback } from 'react';
import { RetentionSettings, RetrySettings } from '../../types';
import {
  getRetentionSettings,
  saveRetentionSettings,
  getRetrySettings,
  saveRetrySettings,
  ConflictError,
} from '../../services/systemApi';

const MIN_RETRY_INTERVAL = 15; // health check runs every 15 minutes

const RetentionSettingsTab: React.FC = () => {
  // ── Retention state ──────────────────────────────────────────────
  const [retentionLoading, setRetentionLoading] = useState(true);
  const [retentionSaving, setRetentionSaving] = useState(false);
  const [retentionError, setRetentionError] = useState<string | null>(null);
  const [retentionSuccess, setRetentionSuccess] = useState<string | null>(null);
  const [retentionErrors, setRetentionErrors] = useState<Record<string, string>>({});
  const [retention, setRetention] = useState({ fileExpirationDays: '', dynamoDbTtlDays: '', s3ArchiveRetentionDays: '' });

  // ── Retry state ──────────────────────────────────────────────────
  const [retryLoading, setRetryLoading] = useState(true);
  const [retrySaving, setRetrySaving] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [retrySuccess, setRetrySuccess] = useState<string | null>(null);
  const [retryErrors, setRetryErrors] = useState<Record<string, string>>({});
  const [retry, setRetry] = useState({ retryIntervalMinutes: '', retryMaxAttempts: '' });

  // ── Load retention ───────────────────────────────────────────────
  const loadRetention = useCallback(async () => {
    setRetentionLoading(true);
    setRetentionError(null);
    try {
      const data: RetentionSettings = await getRetentionSettings();
      setRetention({
        fileExpirationDays: data.fileExpirationDays?.toString() ?? '',
        dynamoDbTtlDays: data.dynamoDbTtlDays?.toString() ?? '',
        s3ArchiveRetentionDays: data.s3ArchiveRetentionDays?.toString() ?? '',
      });
    } catch (err: any) {
      setRetentionError(err.message || 'Unable to load retention settings.');
    } finally {
      setRetentionLoading(false);
    }
  }, []);

  // ── Load retry ───────────────────────────────────────────────────
  const loadRetry = useCallback(async () => {
    setRetryLoading(true);
    setRetryError(null);
    try {
      const data: RetrySettings = await getRetrySettings();
      setRetry({
        retryIntervalMinutes: data.retryIntervalMinutes?.toString() ?? '',
        retryMaxAttempts: data.retryMaxAttempts?.toString() ?? '',
      });
    } catch (err: any) {
      setRetryError(err.message || 'Unable to load retry settings.');
    } finally {
      setRetryLoading(false);
    }
  }, []);

  useEffect(() => { loadRetention(); }, [loadRetention]);
  useEffect(() => { loadRetry(); }, [loadRetry]);

  const isPositiveInt = (v: string) => { const n = Number(v.trim()); return Number.isInteger(n) && n > 0; };

  // ── Save retention ───────────────────────────────────────────────
  const handleSaveRetention = async () => {
    const errors: Record<string, string> = {};
    if (!isPositiveInt(retention.fileExpirationDays)) errors.fileExpirationDays = 'Must be a positive integer';
    if (!isPositiveInt(retention.dynamoDbTtlDays)) errors.dynamoDbTtlDays = 'Must be a positive integer';
    if (!isPositiveInt(retention.s3ArchiveRetentionDays)) errors.s3ArchiveRetentionDays = 'Must be a positive integer';
    setRetentionErrors(errors);
    if (Object.keys(errors).length) return;

    setRetentionSaving(true);
    setRetentionError(null);
    setRetentionSuccess(null);
    try {
      await saveRetentionSettings({
        fileExpirationDays: parseInt(retention.fileExpirationDays, 10),
        dynamoDbTtlDays: parseInt(retention.dynamoDbTtlDays, 10),
        s3ArchiveRetentionDays: parseInt(retention.s3ArchiveRetentionDays, 10),
      });
      setRetentionSuccess('Retention settings saved.');
    } catch (err: any) {
      if (err instanceof ConflictError) setRetentionError('Modified by another user. Please reload.');
      else setRetentionError(err.message || 'Failed to save retention settings.');
    } finally {
      setRetentionSaving(false);
    }
  };

  // ── Save retry ───────────────────────────────────────────────────
  const handleSaveRetry = async () => {
    const errors: Record<string, string> = {};
    const interval = parseInt(retry.retryIntervalMinutes, 10);
    const attempts = parseInt(retry.retryMaxAttempts, 10);
    if (!isPositiveInt(retry.retryIntervalMinutes)) {
      errors.retryIntervalMinutes = 'Must be a positive integer';
    } else if (interval < MIN_RETRY_INTERVAL) {
      errors.retryIntervalMinutes = `Minimum is ${MIN_RETRY_INTERVAL} minutes (health check cadence)`;
    }
    if (!isPositiveInt(retry.retryMaxAttempts)) errors.retryMaxAttempts = 'Must be a positive integer';
    setRetryErrors(errors);
    if (Object.keys(errors).length) return;

    setRetrySaving(true);
    setRetryError(null);
    setRetrySuccess(null);
    try {
      await saveRetrySettings({ retryIntervalMinutes: interval, retryMaxAttempts: attempts });
      setRetrySuccess('Retry settings saved.');
    } catch (err: any) {
      if (err instanceof ConflictError) setRetryError('Modified by another user. Please reload.');
      else setRetryError(err.message || 'Failed to save retry settings.');
    } finally {
      setRetrySaving(false);
    }
  };

  const inputClass = (hasError: boolean) =>
    `mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
      hasError
        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
    }`;

  return (
    <div className="space-y-8">

      {/* ── Retention ─────────────────────────────────────────── */}
      <section>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Retention</h3>

        {retentionError && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-center justify-between" role="alert">
            <span>{retentionError}</span>
            <button onClick={() => { setRetentionError(null); loadRetention(); }} className="ml-4 text-sm font-medium underline">Retry</button>
          </div>
        )}
        {retentionSuccess && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md flex items-center justify-between" role="status">
            <span>{retentionSuccess}</span>
            <button onClick={() => setRetentionSuccess(null)} className="ml-4 text-sm font-medium">✕</button>
          </div>
        )}

        {retentionLoading ? (
          <div className="text-gray-500">Loading...</div>
        ) : (
          <div className="bg-white shadow rounded-lg p-6">
            <div className="space-y-4 max-w-lg">
              {([
                { key: 'fileExpirationDays', label: 'File Expiration (days)', placeholder: 'e.g. 30' },
                { key: 'dynamoDbTtlDays', label: 'DynamoDB TTL (days)', placeholder: 'e.g. 90' },
                { key: 's3ArchiveRetentionDays', label: 'S3 Archive Retention (days)', placeholder: 'e.g. 365' },
              ] as const).map(({ key, label, placeholder }) => (
                <div key={key}>
                  <label htmlFor={`ret-${key}`} className="block text-sm font-medium text-gray-700">{label}</label>
                  <input
                    id={`ret-${key}`}
                    type="number"
                    min="1"
                    step="1"
                    value={retention[key]}
                    onChange={(e) => setRetention(p => ({ ...p, [key]: e.target.value }))}
                    className={inputClass(!!retentionErrors[key])}
                    placeholder={placeholder}
                  />
                  {retentionErrors[key] && <p className="mt-1 text-sm text-red-600">{retentionErrors[key]}</p>}
                </div>
              ))}
            </div>
            <div className="mt-6">
              <button
                onClick={handleSaveRetention}
                disabled={retentionSaving}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {retentionSaving ? 'Saving...' : 'Save Retention Settings'}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ── Retries ───────────────────────────────────────────── */}
      <section>
        <h3 className="text-lg font-medium text-gray-900 mb-1">Retries</h3>
        <p className="text-sm text-gray-500 mb-4">
          When jobs have failed and their source files remain in S3, the health check will
          automatically test the press SMB connection every <strong>Retry Interval</strong> minutes.
          If the connection succeeds, all pending files for that press are requeued automatically.
          After <strong>Max Attempts</strong> consecutive failures, an alert is sent and retrying stops.
          Minimum interval is {MIN_RETRY_INTERVAL} min (health check cadence).
        </p>

        {retryError && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-center justify-between" role="alert">
            <span>{retryError}</span>
            <button onClick={() => { setRetryError(null); loadRetry(); }} className="ml-4 text-sm font-medium underline">Retry</button>
          </div>
        )}
        {retrySuccess && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md flex items-center justify-between" role="status">
            <span>{retrySuccess}</span>
            <button onClick={() => setRetrySuccess(null)} className="ml-4 text-sm font-medium">✕</button>
          </div>
        )}

        {retryLoading ? (
          <div className="text-gray-500">Loading...</div>
        ) : (
          <div className="bg-white shadow rounded-lg p-6">
            <div className="space-y-4 max-w-lg">
              <div>
                <label htmlFor="retry-interval" className="block text-sm font-medium text-gray-700">
                  Retry Interval (minutes)
                </label>
                <input
                  id="retry-interval"
                  type="number"
                  min={MIN_RETRY_INTERVAL}
                  step="1"
                  value={retry.retryIntervalMinutes}
                  onChange={(e) => setRetry(p => ({ ...p, retryIntervalMinutes: e.target.value }))}
                  className={inputClass(!!retryErrors.retryIntervalMinutes)}
                  placeholder={`e.g. ${MIN_RETRY_INTERVAL}`}
                />
                {retryErrors.retryIntervalMinutes
                  ? <p className="mt-1 text-sm text-red-600">{retryErrors.retryIntervalMinutes}</p>
                  : <p className="mt-1 text-xs text-gray-500">Minimum {MIN_RETRY_INTERVAL} minutes</p>
                }
              </div>
              <div>
                <label htmlFor="retry-max" className="block text-sm font-medium text-gray-700">
                  Max Retry Attempts
                </label>
                <input
                  id="retry-max"
                  type="number"
                  min="1"
                  step="1"
                  value={retry.retryMaxAttempts}
                  onChange={(e) => setRetry(p => ({ ...p, retryMaxAttempts: e.target.value }))}
                  className={inputClass(!!retryErrors.retryMaxAttempts)}
                  placeholder="e.g. 5"
                />
                {retryErrors.retryMaxAttempts && <p className="mt-1 text-sm text-red-600">{retryErrors.retryMaxAttempts}</p>}
              </div>
            </div>
            <div className="mt-6">
              <button
                onClick={handleSaveRetry}
                disabled={retrySaving}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {retrySaving ? 'Saving...' : 'Save Retry Settings'}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default RetentionSettingsTab;
